/**
 * CTRL_13 Dashboard - Data Provenance (Detailed) & Lineage Tracking
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Progress } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  DatabaseOutlined,
  LineChartOutlined,
  StarOutlined
} from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

function CTRL_13_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_13';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate provenance coverage
  const provenanceCoverage = useMemo(() => {
    if (events.length === 0) return { total: 0, tracked: 0, percentage: 0 };

    const tracked = events.filter(e => {
      const metadata = e.event_metadata || {};
      return metadata.lineage_tracked === true && metadata.data_provenance;
    }).length;

    return {
      total: events.length,
      tracked,
      percentage: Math.round((tracked / events.length) * 100)
    };
  }, [events]);

  // Calculate data source distribution
  const sourceDistribution = useMemo(() => {
    const distribution = {};

    events.forEach(e => {
      const source = e.event_metadata?.data_source;
      if (source) {
        distribution[source] = (distribution[source] || 0) + 1;
      }
    });

    return Object.entries(distribution).map(([source, count]) => ({
      source,
      count,
      percentage: events.length > 0 ? Math.round((count / events.length) * 100) : 0
    })).sort((a, b) => b.count - a.count);
  }, [events]);

  // Calculate data quality scores
  const qualityStats = useMemo(() => {
    const scores = events
      .map(e => e.event_metadata?.data_quality_score)
      .filter(score => score !== undefined && score !== null);

    if (scores.length === 0) {
      return { average: 0, min: 0, max: 0, distribution: [] };
    }

    const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    const min = Math.min(...scores);
    const max = Math.max(...scores);

    // Distribution by quality bands
    const distribution = {
      excellent: scores.filter(s => s >= 90).length,
      good: scores.filter(s => s >= 70 && s < 90).length,
      fair: scores.filter(s => s >= 50 && s < 70).length,
      poor: scores.filter(s => s < 50).length
    };

    return {
      average: average.toFixed(1),
      min,
      max,
      distribution: [
        { band: 'Excellent (90-100)', count: distribution.excellent, color: '#52c41a' },
        { band: 'Good (70-89)', count: distribution.good, color: '#1890ff' },
        { band: 'Fair (50-69)', count: distribution.fair, color: '#faad14' },
        { band: 'Poor (<50)', count: distribution.poor, color: '#f5222d' }
      ]
    };
  }, [events]);

  // Table columns for lineage tracking
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (ts) => new Date(ts).toLocaleString(),
      width: 180
    },
    {
      title: 'Data Source',
      dataIndex: ['event_metadata', 'data_source'],
      key: 'source',
      render: (source) => source ? <Tag color="blue">{source.toUpperCase()}</Tag> : '-'
    },
    {
      title: 'Provenance',
      dataIndex: ['event_metadata', 'data_provenance'],
      key: 'provenance',
      ellipsis: true,
      render: (provenance) => provenance || '-'
    },
    {
      title: 'Lineage Tracked',
      key: 'lineage',
      align: 'center',
      render: (_, record) => {
        const tracked = record.event_metadata?.lineage_tracked === true;
        return tracked ?
          <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag> :
          <Tag color="red" icon={<WarningOutlined />}>No</Tag>;
      }
    },
    {
      title: 'Quality Score',
      dataIndex: ['event_metadata', 'data_quality_score'],
      key: 'quality',
      align: 'center',
      render: (score) => {
        if (score === undefined || score === null) return '-';

        let color = '#f5222d';
        if (score >= 90) color = '#52c41a';
        else if (score >= 70) color = '#1890ff';
        else if (score >= 50) color = '#faad14';

        return (
          <Tag color={color} icon={<StarOutlined />}>
            {score.toFixed(0)}
          </Tag>
        );
      }
    },
    {
      title: 'Metadata Complete',
      key: 'metadata',
      align: 'center',
      render: (_, record) => {
        const complete = record.event_metadata?.metadata_complete === true;
        return complete ?
          <Tag color="green">Complete</Tag> :
          <Tag color="orange">Incomplete</Tag>;
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <Empty
        description="Please select a project to view dashboard"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_13: Data Provenance & Lineage Dashboard</h2>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Lineage Coverage"
              value={provenanceCoverage.percentage}
              suffix="%"
              prefix={provenanceCoverage.percentage >= 90 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: provenanceCoverage.percentage >= 90 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {provenanceCoverage.tracked} of {provenanceCoverage.total} tracked
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg Quality Score"
              value={qualityStats.average}
              prefix={<StarOutlined />}
              valueStyle={{
                color: qualityStats.average >= 90 ? '#52c41a' : qualityStats.average >= 70 ? '#1890ff' : '#faad14'
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Data Sources"
              value={sourceDistribution.length}
              prefix={<DatabaseOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Events"
              value={events.length}
              prefix={<LineChartOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Data Source Distribution */}
      <Card title="Data Source Distribution" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          {sourceDistribution.slice(0, 6).map((item, idx) => (
            <Col xs={24} sm={12} md={8} key={idx} style={{ marginBottom: '16px' }}>
              <Card size="small">
                <Statistic
                  title={item.source.toUpperCase()}
                  value={item.count}
                  suffix={`(${item.percentage}%)`}
                  valueStyle={{ color: '#1890ff' }}
                />
                <Progress
                  percent={item.percentage}
                  strokeColor="#1890ff"
                  size="small"
                  style={{ marginTop: '8px' }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Data Quality Distribution */}
      <Card title="Data Quality Distribution" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          {qualityStats.distribution.map((item, idx) => (
            <Col xs={24} sm={12} md={6} key={idx}>
              <Card size="small" style={{ backgroundColor: `${item.color}15`, borderColor: item.color }}>
                <Statistic
                  title={item.band}
                  value={item.count}
                  valueStyle={{ color: item.color }}
                />
                <Progress
                  percent={events.length > 0 ? Math.round((item.count / events.length) * 100) : 0}
                  strokeColor={item.color}
                  size="small"
                  style={{ marginTop: '8px' }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Lineage Tracking Table */}
      <Card title="Data Lineage Tracking">
        <Table
          dataSource={events.slice(0, 20)}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} events`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_13_Dashboard;
